import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Paper,
  Box,
  useTheme,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Trash2, FileText, Download } from 'lucide-react';
import { FileItem } from '../../types/file';
import { formatFileSize } from '../../utils/fileUtils';
import { format } from 'date-fns';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';

interface FileListProps {
  files: FileItem[];
  onDelete: (id: string) => void;
  deletingFileId?: string | null;
  onDownload: (fileName: string) => void;
  downloading?: Record<string, boolean>;
}

const FileList: React.FC<FileListProps> = ({
  files,
  onDelete,
  deletingFileId,
  onDownload,
  downloading,
}) => {
  const theme = useTheme();
  const { role } = useAuth();
  const isNotUser = role !== AUTH_CONSTANTS.ROLES.USER;

  if (files.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 8,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          border: `1px dashed ${theme.palette.divider}`,
        }}
      >
        <FileText
          size={48}
          color={theme.palette.text.secondary}
          style={{ marginBottom: 16, opacity: 0.5 }}
        />
        <Typography color="text.secondary">No files uploaded yet</Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>
              File Name
            </TableCell>
            <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Size</TableCell>
            <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Type</TableCell>
            <TableCell sx={{ py: 1, olor: theme.palette.text.secondary }}>Last Modified</TableCell>
            <TableCell align="right" sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map(file => (
            <TableRow
              key={file.id}
              hover
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: `${theme.palette.primary.main}10`,
                      borderRadius: 999,
                      p: 0.75,
                      display: 'flex',
                    }}
                  >
                    <FileText size={18} color={theme.palette.primary.main} />
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: 16 }}>
                    {file.name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{formatFileSize(file.size)}</TableCell>
              <TableCell>
                <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    backgroundColor: `${theme.palette.primary.main}10`,
                    color: theme.palette.primary.main,
                  }}
                >
                  {file.type}
                </Box>
              </TableCell>
              <TableCell>{format(new Date(file.lastModified), 'MMM d, yyyy h:mm a')}</TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={() => onDownload(file.name)}
                      disabled={downloading?.[file.name]}
                    >
                      {downloading?.[file.name] ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Download size={20} />
                      )}
                    </IconButton>
                  </Tooltip>
                  {isNotUser ? (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(file.id)}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Trash2 size={20} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FileList;
